import styled from "@emotion/styled";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import CalculateIcon from "@mui/icons-material/Calculate";
import CorporateFareIcon from "@mui/icons-material/CorporateFare";
import EditIcon from "@mui/icons-material/Edit";
import ErrorIcon from "@mui/icons-material/Error";
import HowToRegIcon from "@mui/icons-material/HowToReg";
import SearchIcon from "@mui/icons-material/Search";
import MUIButton, { type ButtonProps as MUIButtonProps } from "@mui/material/Button";
import React from "react";
import { Colors } from "../../constants/Colors";
import { Paragraph } from "../../constants/Typography";

type ButtonIconType = "calculate" | "edit" | "error" | "howToReg" | "search" | "attachFile" | "corporateFare";

type ButtonProps = {
  /**
   * The text that is displayed on the button.
   */
  label: string;

  /**
   * [Optional] An icon that is displayed on the button.
   */
  fronticon?: ButtonIconType;

  /**
   * [Optional] The design variation of the button.
   * It can be either "small", "medium", "large" or "XLarge".
   * @default "medium"
   */
  size?: MUIButtonProps["size"] | "XLarge";
  component?: React.ElementType;
} & Omit<MUIButtonProps, "size" | "component">;

const calcIconSizeBySize = (size: ButtonProps["size"]): number => {
  return size === "small" ? 17.33 : 24;
};

const calcFontSizeBySize = (size: ButtonProps["size"]): number => {
  return size === "XLarge" ? Paragraph.XL : Paragraph.M;
};

const calcPaddingBySize = (size: ButtonProps["size"]): string => {
  switch (size) {
    case "small":
      return "4px 24px";
    case "medium":
      return "8px 32px";
    case "large":
      return "12px 40px";
    case "XLarge":
      return "16px 48px";
    // default is set "M"
    default:
      return "8px 32px";
  }
};

const calcStyleByVariant = (variant: ButtonProps["variant"], color?: MUIButtonProps["color"]) => {
  if (color === "primary") {
    switch (variant) {
      case "outlined":
        return `
        border: 1px solid ${Colors.Facebook};
        color: ${Colors.Facebook};

        &:disabled {
          background: ${Colors.Border.main};
        }
      `;
      case "contained":
        return `
        background:  ${Colors.Facebook};
        border: none;
        color: ${Colors.White};

        &:hover {
          background: ${Colors.Primary.light};
        }

        &:disabled {
          background: ${Colors.Border.main};
        }
      `;
    }
  }

  if (color === "secondary") {
    switch (variant) {
      case "outlined":
        return `
        position: relative;
        border: 1px solid ${Colors.Border.main};
        background: ${Colors.White};
        
        span {
          background: linear-gradient(95.5deg, ${Colors.Button.main.left} 5.99%, ${Colors.Button.main.right} 100%);
          background-clip: text;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
        }

        &::before {
          content: '';
          position: absolute;
          top: -1px;
          left: -1px;
          right: -1px;
          bottom: -1px;
          border-radius: 999px;
          background: linear-gradient(95.5deg, ${Colors.Button.main.left} 5.99%, ${Colors.Button.main.right} 100%);
          opacity: 0;
          transition: opacity 0.2s;
          z-index: -1;
        }

        &::after {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          border-radius: 999px;
          background: ${Colors.White};
          z-index: -1;
        }

        &:hover {
          border-color: transparent;
          
          &::before {
            opacity: 1;
          }
        }

        &:disabled {
          background: ${Colors.White};
          border: 1px solid ${Colors.Border.main};
          
          span {
            background: ${Colors.Border.main};
            background-clip: text;
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
          }
        }
      `;
      case "contained":
        return `
        background: linear-gradient(95.5deg, ${Colors.Button.main.left} 5.99%, ${Colors.Button.main.right} 100%);
        border: none;
        color: ${Colors.White};

        &:hover {
          background: ${Colors.Secondary.main};
        }

        &:disabled {
          background: ${Colors.Border.main};
        }
      `;
    }
  }
};

const Icon = (props: {
  iconName: ButtonIconType | undefined;
  variant: ButtonProps["variant"];
  size: ButtonProps["size"];
  disabled: ButtonProps["disabled"];
}) => {
  const { iconName, variant, size, disabled } = props;
  if (!iconName) return null;

  const sizeNum = calcIconSizeBySize(size);
  const style: React.CSSProperties = {
    width: `${sizeNum}px`,
    height: `${sizeNum}px`,
    color: variant === "contained" ? Colors.White : Colors.Button.main.left,
    ...(disabled && variant === "outlined" && { color: Colors.Border.main }),
  };

  switch (iconName) {
    case "calculate":
      return <CalculateIcon style={style} />;
    case "edit":
      return <EditIcon style={style} />;
    case "error":
      return <ErrorIcon style={style} />;
    case "howToReg":
      return <HowToRegIcon style={style} />;
    case "search":
      return <SearchIcon style={style} />;
    case "attachFile":
      return <AttachFileIcon style={style} />;
    case "corporateFare":
      return <CorporateFareIcon style={style} />;
    default:
      return null;
  }
};

// render の外で定義し、親の再描画でもボタンのDOMとフォーカスを維持する。
// 独自の XLarge を含むサイズは、MUI の size やDOM属性へ渡さずスタイルに反映する。
const StyledButton = styled(MUIButton, {
  shouldForwardProp: (prop) => prop !== "buttonSize" && prop !== "theme",
})<{ buttonSize: ButtonProps["size"] }>`
  font-weight: bold;
  width: fit-content;
  height: fit-content;
  display: flex;
  align-items: center;
  font-size: ${({ buttonSize }) => calcFontSizeBySize(buttonSize)}px;
  padding: ${({ buttonSize }) => calcPaddingBySize(buttonSize)};
  border-radius: 999px;
  ${({ variant, color }) => calcStyleByVariant(variant, color)}
  backface-visibility: hidden;
  // filled の時に hover すると背景色がチラつくために none にしている
  transition: none;
`;

const Button = ({
  variant = "contained",
  size = "medium",
  type = "button",
  color = "secondary",
  ...props
}: ButtonProps) => {
  return (
    <StyledButton
      buttonSize={size}
      disabled={props.disabled}
      startIcon={<Icon iconName={props.fronticon} variant={variant} size={size} disabled={props.disabled} />}
      type={type}
      component={props.component || "button"}
      color={color}
      variant={variant}
      {...props}
    >
      <span style={{ whiteSpace: "pre-line" }}>{props.label}</span>
    </StyledButton>
  );
};
export default Button;
